import React, { useState } from 'react';

import { useRouter } from 'next/router';
import { useInfiniteQuery, useMutation } from 'react-query';
import UseToast from 'hooks/use-toast';

import { ReportFeedbackService } from 'services/core';

import { CourseComments, LectureComments } from 'services/core';

import {
	CONTENT_COMMENTS_CACHE_TIME,
	CONTENT_COMMENTS_PAGE_SIZE,
	contentCommentsQueryKey
} from 'constants/content-comments.const';

import ActionButton from '@core/components/actionbutton/ActionButton';
import Spinner from '@core/components/spinner/Spinner';

import StarRating from 'assets/icons/star-rating.svg';
import ArrowRight from 'assets/icons/arrow-right.svg';
import ReportIcon from '@core/assets/icons/report-icon.svg';

import classes from './ContentComments.module.scss';

const ContentComments = ({
	contentType,
	className,
	tabSetterHandler,
	deviceType,
	listDirection
}) => {
	const router = useRouter();
	const [pageSize] = useState(CONTENT_COMMENTS_PAGE_SIZE);
	const { ToastContainer, toast } = UseToast();
	const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery(
		contentCommentsQueryKey(contentType, router?.query?.slug[0]),
		({ pageParam = 1 }) =>
			contentType === 'course'
				? CourseComments({ id: router?.query?.slug[0], page_size: pageSize, page: pageParam })
				: LectureComments({ id: router?.query?.slug[0], page_size: pageSize, page: pageParam }),
		{
			getNextPageParam: (lastpage, pages) => {
				if (lastpage.data?.next) {
					return pages.length + 1;
				}
				return;
			},
			// The first page is prefetched server side, so keep it instead of
			// refetching on mount. Submitting a review refetches explicitly.
			cacheTime: CONTENT_COMMENTS_CACHE_TIME,
			staleTime: CONTENT_COMMENTS_CACHE_TIME
		}
	);

	// report Feedback
	const { mutate: sendFeedbackReport } = useMutation(ReportFeedbackService, {
		onSuccess: (data) => {
			toast(data.data.message);
		}
	});
	return (
		<>
			<ToastContainer />
			{data && !isLoading ? (
				<>
					{listDirection === 'column' ? (
						<>
							<div className={`${classes['content-comments-wrapper-column']} ${className || ''}`}>
								{data.pages?.map((page) =>
									page.data.results.map((res) => (
										<div
											key={res.id}
											className={`${classes['comment-card']} d-flex flex-column w-100`}>
											<div className="d-flex flex-row align-items-center">
												<h4>{res.name}</h4>
												<span />
												<StarRating data-testid="star-rating" />
												<h5>{res.rate}</h5>
											</div>
											<h6>Reviewed on {res.date}</h6>
											<p>{res.comment}</p>
											<span className="d-flex flex-row justify-content-end">
												<button onClick={() => sendFeedbackReport(res.id)}>
													Report review
													<ReportIcon data-testid="report-icon" />
												</button>
											</span>
										</div>
									))
								)}
								{hasNextPage && !isFetchingNextPage ? (
									<ActionButton kind="blank" loading={isLoading} onClick={() => fetchNextPage()}>
										More reviews...
									</ActionButton>
								) : (
									isFetchingNextPage && (
										<div className="d-flex align-items-center justify-content-center w-100 py-5">
											<Spinner />
										</div>
									)
								)}
							</div>
						</>
					) : (
						<>
							<div className={`${classes['content-comments-wrapper']} ${className || ''}`}>
								{data.pages?.map((page) =>
									page.data.results.map((res) => (
										<div
											key={res.id}
											className={`${classes['comment-card']} d-flex flex-column w-100`}>
											<div className="d-flex flex-row align-items-center">
												<h4>{res.name}</h4>
												<span />
												<StarRating data-testid="star-rating" />
												<h5>{res.rate}</h5>
											</div>
											<h6>Reviewed on {res.date}</h6>
											<p>{res.comment}</p>
											<span className="d-flex flex-row justify-content-end">
												<button onClick={() => sendFeedbackReport(res.id)}>
													Report review
													<ReportIcon data-testid="report-icon" />
												</button>
											</span>
										</div>
									))
								)}
							</div>
							{deviceType === 'mobile' ? (
								data.pages[0].data.count !== 0 && (
									<button
										className={`${classes['comments-show-more-btn']} align-self-start`}
										onClick={() => tabSetterHandler('reviews')}>
										See all reviews <ArrowRight />
									</button>
								)
							) : hasNextPage && !isFetchingNextPage ? (
								<ActionButton kind="blank" loading={isLoading} onClick={() => fetchNextPage()}>
									More reviews...
								</ActionButton>
							) : (
								isFetchingNextPage && (
									<div className="d-flex align-items-center justify-content-center w-100 py-5">
										<Spinner />
									</div>
								)
							)}
						</>
					)}
				</>
			) : (
				<div className="d-flex align-items-center justify-content-center w-100 py-5">
					<Spinner />
				</div>
			)}
		</>
	);
};

export default React.memo(ContentComments);
